interface Shortcuts {
  [key: string]: boolean | (() => void)[];
  enabled: boolean;
}

export interface ShortcutConfig {
  callback: () => void;
  key: string;
}

export const shortcuts: Shortcuts = {
  enabled: true,
};

function validShortcutFocus(target: HTMLElement): boolean {
  const invalidTargetsType = ["text", "textarea", "search", "number"];
  const invalidElements = ["input", "textarea", "select", "button"];
  const invalidClassNames = ["ProseMirror", "ql-editor"];
  const invalidAttributes = ["contenteditable"];
  const invalidIds: string[] = [];

  return (
    !invalidElements.includes(target.tagName.toLowerCase()) &&
    !invalidTargetsType.includes((target as HTMLInputElement).type) &&
    !invalidAttributes.some((attribute) => target.getAttribute(attribute)) &&
    !invalidClassNames.some((invalidClassName) =>
      target.classList.contains(invalidClassName)
    ) &&
    !invalidIds.includes(target.id)
  );
}

function getShortcutKey(event: KeyboardEvent): string {
  const { altKey, ctrlKey, key, metaKey, shiftKey } = event;

  const shortcutKeys: string[] = [];

  if (ctrlKey) shortcutKeys.push("Control");
  if (altKey) shortcutKeys.push("Alt");
  if (shiftKey) shortcutKeys.push("Shift");
  if (metaKey) shortcutKeys.push("Meta");

  if (!shortcutKeys.some((shortcutKey) => shortcutKey === key)) {
    shortcutKeys.push(key);
  }

  return shortcutKeys.join("+");
}

function runCallbacks(event: KeyboardEvent): void {
  if (!shortcuts.enabled) return;

  const shortcutKey = getShortcutKey(event);

  if (
    shortcuts[shortcutKey] &&
    validShortcutFocus(event.target as HTMLElement)
  ) {
    event.preventDefault();

    (shortcuts[shortcutKey] as (() => void)[]).forEach((callback) => {
      if (callback) callback.call(event);
    });
  }
}

export const addKeydownListener = (): void => {
  window.addEventListener("keydown", (event) => runCallbacks(event));
};

export const addShortcut = ({ callback, key }: ShortcutConfig): void => {
  if (!shortcuts[key]) {
    shortcuts[key] = [callback];
  } else {
    (shortcuts[key] as (() => void)[]).push(callback);
  }
};

export const addShortcutList = (shortcutList: ShortcutConfig[]): void => {
  shortcutList.forEach((shortcut) => addShortcut(shortcut));
};

export const removeShortcut = (
  shortcutKey: string,
  callback?: () => void
): void => {
  const callbacks = shortcuts[shortcutKey];

  if (!Array.isArray(callbacks)) return;

  if (!callback) {
    callbacks.pop();

    return;
  }

  const index = callbacks.lastIndexOf(callback);

  if (index !== -1) callbacks.splice(index, 1);
};

export const removeShortcutList = (shortcutList: ShortcutConfig[]): void => {
  shortcutList.forEach(({ callback, key }) => removeShortcut(key, callback));
};

export const disableShortcuts = (): void => {
  shortcuts.enabled = false;
};

export const enableShortcuts = (): void => {
  shortcuts.enabled = true;
};
